/* Supps cart/product core: ported from canonical Dawn (live theme) to register the cart & product custom elements this rebuild theme was missing. */
(function(){
"use strict";
let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    const promises = subscribers[eventName]
      .map((callback) => callback(data))
    return Promise.all(promises);
  } else {
    return Promise.resolve()
  }
}

const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};


class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}
if(!customElements.get('variant-selects')){customElements.define('variant-selects',VariantSelects);}


try{window.getFocusableElements=getFocusableElements;}catch(e){}

/* CartPerformance stub -- telemetry not loaded; no-op so cart/product-form continue working */
const CartPerformance = {
  createStartingMarker: () => null,
  measure: (label, fn) => { if (typeof fn === 'function') fn(); },
  measureFromMarker: () => {},
  measureFromEvent: () => {},
};
try{window.trapFocus=trapFocus;}catch(e){}
try{window.removeTrapFocus=removeTrapFocus;}catch(e){}
try{window.onKeyUpEscape=onKeyUpEscape;}catch(e){}
try{window.debounce=debounce;}catch(e){}
try{window.fetchConfig=fetchConfig;}catch(e){}
try{window.CartPerformance=CartPerformance;}catch(e){}
try{window.pauseAllMedia=pauseAllMedia;}catch(e){}
try{window.SectionId=SectionId;}catch(e){}
try{window.HTMLUpdateUtility=HTMLUpdateUtility;}catch(e){}
try{window.subscribe=subscribe;}catch(e){}
try{window.publish=publish;}catch(e){}
try{window.PUB_SUB_EVENTS=PUB_SUB_EVENTS;}catch(e){}
try{window.ON_CHANGE_DEBOUNCE_TIMER=ON_CHANGE_DEBOUNCE_TIMER;}catch(e){}
})();

/* ---------------------------------------------------------------------------
 * Supps discount codes -- multi-code, backend-decides-eligibility model.
 * Jamie, 2026-08-28: "I want people to have the ability to apply codes and have
 * them accepted. The backend figures out which codes are eligible... If they
 * type in two codes they are both eligible. They don't stack. You just give
 * them whichever one is the bigger discount."
 *
 * How it works, all VERIFIED live before this was written:
 *  - cartDiscountCodesUpdate SETS the whole list, it does not append. Sending A
 *    then B leaves the cart holding only B. So every call sends a full list.
 *  - The cart happily holds a code that is not currently eligible, and Shopify
 *    re-evaluates `applicable` per code on every cart change, by itself.
 *    Proven: SUPPS20 read applicable:true on a one-time line and flipped to
 *    applicable:false the instant that line became a subscription.
 *  - Codes SURVIVE a /cart/change.js plan switch.
 *  - SEQUENCING MATTERS. Firing /cart/change.js immediately after a discount
 *    mutation returned not-ok and left the line unswitched; the same call with a
 *    /cart.js read in between returned 200. So every discount write is followed
 *    by a /cart.js read before anything else touches the cart.
 *
 * The entered codes are the master record and live in a cart attribute, so they
 * outlive drawer re-renders and page loads. The cart's own discount list is
 * derived from them, never the other way round.
 * ------------------------------------------------------------------------- */
(function () {
  var TOKEN = '3344373e62d2e8194d3f003af82a2916';
  var ATTR = 'supps_codes';

  var MUTATION =
    'mutation($cartId: ID!, $codes: [String!]) { cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $codes) {' +
    ' cart { discountCodes { code applicable } cost { totalAmount { amount currencyCode } subtotalAmount { amount currencyCode } } }' +
    ' userErrors { field message } } }';

  var READ_CODES = 'query($id: ID!){ cart(id:$id){ discountCodes { code applicable } } }';

  function storefront(query, variables) {
    return fetch('/api/2025-01/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': TOKEN },
      body: JSON.stringify({ query: query, variables: variables })
    }).then(function (r) { return r.json(); });
  }

  function readCart() { return fetch('/cart.js').then(function (r) { return r.json(); }); }

  function enteredFrom(cart) {
    var raw = (cart.attributes && cart.attributes[ATTR]) || '';
    return raw ? raw.split(',').filter(Boolean) : [];
  }

  // Written BEFORE the discount mutation, never after, so the discount write is
  // always the last thing to touch the cart (see SEQUENCING above).
  function saveEntered(list) {
    var attrs = {};
    attrs[ATTR] = list.join(',');
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) { return r.json(); });
  }

  function setCodes(gid, codes) {
    return storefront(MUTATION, { cartId: gid, codes: codes }).then(function (j) {
      var d = j && j.data && j.data.cartDiscountCodesUpdate;
      if (!d || !d.cart) throw new Error('discount update failed');
      return {
        codes: d.cart.discountCodes || [],
        total: parseFloat(d.cart.cost.totalAmount.amount),
        subtotal: parseFloat(d.cart.cost.subtotalAmount.amount),
        currency: d.cart.cost.totalAmount.currencyCode || 'GBP'
      };
    });
  }

  /* Reconcile the cart against every code the shopper has entered.
     Each code is priced ON ITS OWN and the one giving the lowest total wins.
     That is deliberate, and it is why we do not simply hand Shopify the whole
     list: these codes do not stack (10 of the 12 live ones combine with
     nothing), so when several are sent together SHOPIFY picks which survives
     and marks the rest not-applicable -- and its pick is not the biggest
     discount. Measured on a 2-pack one-time cart: SUPPS20 36.00, GYMTEL20
     36.00, OFF10% 40.50, WIN10 40.50, WIN4 41.00. Send SUPPS20 + OFF10%
     together and whichever Shopify prefers survives, so the shopper could be
     handed the 10% over the 20%. Pricing each code and setting the cart to the
     winner alone is the only way to guarantee Jamie's bigger-discount rule.

     Losing codes are NOT left on the cart's discount list, because a competing
     applicable code can displace the winner. They live in the cart attribute
     instead, and every reconcile re-prices the whole entered list -- so after a
     plan switch a code that was losing, or ineligible, can become the winner. */
  function reconcile(newCode) {
    var entered, gid, priced = [];
    return readCart()
      .then(function (cart) {
        gid = 'gid://shopify/Cart/' + cart.token;
        entered = enteredFrom(cart);
        // ADOPT codes already on the cart that this module did not put there.
        // The prize popup applies WIN10 / WIN4 by fetch, straight onto the cart
        // and never through here, so they are absent from the attribute. Without
        // this union the next reconcile would set the list from the attribute
        // alone and destroy the shopper's popup discount.
        return storefront(READ_CODES, { id: gid }).then(function (j) {
          var live = (j && j.data && j.data.cart && j.data.cart.discountCodes) || [];
          live.forEach(function (row) {
            var c = String(row.code).toUpperCase();
            if (entered.indexOf(c) === -1) entered.push(c);
          });
          if (newCode) {
            var up = String(newCode).trim().toUpperCase();
            if (up && entered.indexOf(up) === -1) entered.push(up);
          }
          if (!entered.length) return null;
          /* ONLY write the attribute when the list actually changed.
             supps-free-bag.js treats /cart/update as a cart mutation and runs
             its own reconcile off the back of it. This module refreshes on every
             supps:cart-updated, so an unconditional write fired that reconcile a
             second time moments after a plan switch, while the cart was still
             settling -- it read a stale cart, decided the bag was not required
             and DELETED the bag the first reconcile had just added. Measured:
             bag present at 900ms, gone at 1800ms, never came back. A refresh
             that changes nothing must not write to the cart at all. */
          var before = enteredFrom(cart).join(',');
          if (entered.join(',') === before) return readCart();
          return saveEntered(entered).then(readCart);
        });
      })
      .then(function (cart) {
        if (!cart) return null;
        return entered.reduce(function (chain, code) {
          return chain.then(function () {
            return setCodes(gid, [code]).then(function (r) {
              var row = r.codes[0];
              priced.push({ code: code, applicable: !!(row && row.applicable), total: r.total });
            });
          });
        }, Promise.resolve());
      })
      .then(function () {
        if (!entered.length) return null;
        var best = null;
        priced.forEach(function (p) {
          if (p.applicable && (best === null || p.total < best.total)) best = p;
        });
        return setCodes(gid, best ? [best.code] : []).then(function (res) {
          return { entered: entered, priced: priced, winner: best ? best.code : null, result: res };
        });
      })
      .then(function (state) {
        if (!state) return null;
        // Resync the AJAX cart before anything else is allowed to write to it.
        return readCart().then(function () {
          /* EVERY cartDiscountCodesUpdate DROPS the free gift bag line.
             Proven directly: a cart holding the 2-pack subscription AND the bag
             was sent one discount mutation, and the mutation's OWN response came
             back listing a single line, the 2-pack. The bag was gone from
             /cart.js straight after, with no /cart/ request having removed it --
             the Storefront cart API silently discards it. The product is not at
             fault: Extra Free Bag is ACTIVE, published to Online Store and
             availableForSale.

             This is not new. The previous discount handler used the same
             mutation, so applying a code has always been quietly destroying the
             free bag; doing it on every refresh simply made it constant. The
             module repairs the damage it causes, once, after all its mutations
             have finished. supps-free-bag.js is the single owner of that line
             and its reconcile re-adds it only when the cart genuinely
             qualifies. */
          if (window.SuppsFreeBag && typeof window.SuppsFreeBag.reconcile === 'function') {
            window.SuppsFreeBag.reconcile();
          }
          return state;
        });
      });
  }

  /* Removal has to clear the cart's discount list BEFORE reconciling. reconcile
     adopts whatever is on the cart (so popup-applied codes survive), so if the
     removed code were still sitting there as the winner it would be adopted
     straight back and the x button would appear to do nothing. */
  function removeCode(code) {
    var remaining;
    return readCart()
      .then(function (cart) {
        remaining = enteredFrom(cart).filter(function (c) { return c !== code; });
        return saveEntered(remaining).then(function () {
          return setCodes('gid://shopify/Cart/' + cart.token, []);
        });
      })
      .then(readCart)
      .then(function () { return reconcile(null); });
  }

  function money(amount, currency) {
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(amount);
    } catch (e) {
      return '£' + Number(amount).toFixed(2);
    }
  }

  /* Chips render inside the SUBMITTED form's own host, so a page carrying more
     than one discount form -- the homepage drawer carries a legacy one ahead of
     the redesigned one -- can never paint another form's state. */
  function renderChips(form, codes) {
    if (!form) return;
    var host = form.parentElement || form;
    var box = host.querySelector('[data-supps-code-chips]');
    if (!box) {
      box = document.createElement('div');
      box.setAttribute('data-supps-code-chips', '');
      box.className = 'supps-codes';
      host.appendChild(box);
    }
    box.innerHTML = '';
    (codes || []).forEach(function (c) {
      var chip = document.createElement('span');
      chip.className = 'supps-code' + (c.applicable ? ' supps-code--on' : '');
      var label = document.createElement('span');
      label.className = 'supps-code__t';
      label.textContent = c.code;
      var x = document.createElement('button');
      x.type = 'button';
      x.className = 'supps-code__x';
      x.setAttribute('aria-label', 'Remove ' + c.code);
      x.textContent = '×';
      x.addEventListener('click', function () {
        x.disabled = true;
        removeCode(c.code).then(function (state) { paint(form, state); });
      });
      chip.appendChild(label);
      chip.appendChild(x);
      box.appendChild(chip);
    });
    box.style.display = (codes && codes.length) ? '' : 'none';
  }

  function paintTotal(total, currency) {
    document.querySelectorAll('.cart-drawer__totals__row__money').forEach(function (el) {
      el.textContent = money(total, currency);
    });
  }

  /* The cart's own discount list holds only the winner, so the chips are drawn
     from the priced list of everything entered: lit means this code is the one
     currently discounting the cart, dim means held and waiting. */
  function paint(form, state) {
    if (!state) { renderChips(form, []); return null; }
    renderChips(form, state.priced.map(function (p) {
      return { code: p.code, applicable: p.code === state.winner };
    }));
    paintTotal(state.result.total, state.result.currency);
    return state;
  }

  function refresh(form) {
    return reconcile(null).then(function (state) { return paint(form, state); });
  }

  window.SuppsDiscounts = {
    reconcile: reconcile, refresh: refresh, renderChips: renderChips,
    remove: removeCode, money: money, paintTotal: paintTotal, paint: paint
  };
})();

/* Every element is looked up on the SUBMITTED form. The previous version
   re-found them with document.querySelector after its await, which on the
   homepage -- where the drawer carries a legacy .cart-discount form ahead of the
   redesigned .supps-discount one -- put the message on the wrong form and
   re-enabled the wrong button, leaving the real ADD button disabled for good.
   That was the "apply one code, then it does nothing" bug. */
async function handleDiscountForm(event) {
  event.preventDefault();
  const form = event.target;
  // The error <p> is a SIBLING of the form inside .supps-discount, not a child,
  // so a form-only lookup finds nothing and an invalid code says nothing at all.
  // Widen to the form's own host: still scoped, never document-wide, so a page
  // with two discount forms can never paint the other one's error.
  const scope = form.parentElement || form;
  const input = form.querySelector('[name="cart-discount-field"]');
  const errorEl = form.querySelector('.cart-discount-form__error') ||
                  scope.querySelector('.cart-discount-form__error');
  const btn = form.querySelector('button[type="submit"]');
  const code = (input ? input.value : '').trim();

  if (!code) {
    if (errorEl) { errorEl.textContent = 'Please enter a discount code!'; errorEl.style.display = 'block'; }
    return;
  }
  if (errorEl) errorEl.style.display = 'none';
  if (btn) btn.disabled = true;

  try {
    const state = await window.SuppsDiscounts.reconcile(code);
    const existing = scope.querySelector('.cart-discount-form__success');
    if (existing) existing.remove();

    window.SuppsDiscounts.paint(form, state);

    if (state) {
      const up = code.toUpperCase();
      const row = state.priced.find(function (p) { return p.code === up; });
      const isWinner = state.winner === up;
      const saved = state.result.subtotal - state.result.total;

      if (isWinner) {
        const successEl = document.createElement('p');
        successEl.className = 'cart-discount-form__success';
        successEl.textContent = saved > 0
          ? 'Code applied. You save ' + window.SuppsDiscounts.money(saved, state.result.currency) + '!'
          : 'Code applied.';
        form.appendChild(successEl);
        if (input) input.value = '';
      } else if (row && row.applicable) {
        // A real code, held, but a bigger discount is already on the cart. No
        // message: the dim chip is the feedback, and Jamie has not written copy
        // for this case. Clearing the input shows it was accepted.
        if (input) input.value = '';
      } else if (errorEl) {
        errorEl.textContent = "That code isn't valid.";
        errorEl.style.display = 'block';
      }
    }
  } catch (e) {
    if (errorEl) { errorEl.textContent = 'Something went wrong. Try again.'; errorEl.style.display = 'block'; }
  } finally {
    if (btn) btn.disabled = false;
  }
}

function handleDiscountFormChange(event) {
  const form = event.target.closest('form');
  const scope = form && (form.parentElement || form);
  const errorEl = form && (form.querySelector('.cart-discount-form__error') ||
                           scope.querySelector('.cart-discount-form__error'));
  const successEl = form && (form.querySelector('.cart-discount-form__success') ||
                             scope.querySelector('.cart-discount-form__success'));
  if (errorEl) errorEl.style.display = 'none';
  if (successEl) successEl.remove();
}

/* Re-paint held codes whenever the drawer re-renders (plan switch, quantity
   change), so eligibility that flipped server-side shows immediately. */
document.addEventListener('supps:cart-updated', function () {
  var form = document.querySelector('#CartDrawer .supps-discount .cart-discount-form') ||
             document.querySelector('#CartDrawer .cart-discount-form');
  if (form && window.SuppsDiscounts) window.SuppsDiscounts.refresh(form);
});

if (!customElements.get('quantity-input')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.quantity__input');
      this.changeEvent = new Event('change', { bubbles: true });
      this.querySelectorAll('.quantity__button').forEach(
        (btn) => btn.addEventListener('click', this.onButtonClick.bind(this))
      );
      this.input.addEventListener('change', this.validateQtyRules.bind(this));
    }

    connectedCallback() {
      this.validateQtyRules();
    }

    onButtonClick(event) {
      event.preventDefault();
      const prev = this.input.value;
      event.target.closest('button').name === 'plus' ? this.input.stepUp() : this.input.stepDown();
      if (prev === this.input.value) return;
      // Bypass the debounce for button clicks — call updateQuantity directly for instant response
      const cartItems = this.closest('cart-drawer-items') || this.closest('cart-items');
      if (cartItems && typeof cartItems.updateQuantity === 'function') {
        cartItems.updateQuantity(
          this.input.dataset.index,
          parseInt(this.input.value),
          { currentTarget: cartItems },
          this.input.getAttribute('name'),
          this.input.dataset.quantityVariantId
        );
      } else {
        this.input.dispatchEvent(this.changeEvent);
      }
    }

    validateQtyRules() {
      const value = parseInt(this.input.value);
      if (this.input.min) {
        const min = parseInt(this.input.min);
        const btnMinus = this.querySelector(".quantity__button[name='minus']");
        if (btnMinus) btnMinus.classList.toggle('disabled', value <= min);
      }
      if (this.input.max) {
        const max = parseInt(this.input.max);
        const btnPlus = this.querySelector(".quantity__button[name='plus']");
        if (btnPlus) btnPlus.classList.toggle('disabled', value >= max);
      }
    }
  }
  customElements.define('quantity-input', QuantityInput);
}
